import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─── Types ─── */
type ModalPhase = 'select' | 'email' | 'paypal-checkout' | 'processing' | 'success';
type PaymentMethodId = 'paypal' | 'stripe' | 'wise';

interface PlanInfo {
  id: string;
  name: string;
  nameKr: string;
  price: number;
  originalPrice: number | null;
  desc: string;
  features: string[];
  isLaunch?: boolean;
}

interface PaymentGatewayModalProps {
  isOpen: boolean;
  onClose: () => void;
  price?: number;
  productName?: string;
}

/* ─── Constants ─── */
const PAYPAL_CLIENT_ID = 'ASqxzPDJqP6t1EmR8Vth_Vzs1AE2eKm8XOhQkVS7MrZCieWn38JTmLhCo8za87s9r3tHlDK34aIyeQzF';
const PLAN_SDK_SCRIPT_ID = 'paypal-sdk-gateway-capture';

const DEFAULT_PLAN: PlanInfo = {
  id: 'premium', name: 'Premium', nameKr: '프리미엄',
  price: 29.99, originalPrice: 59,
  desc: '사주 · 점성술 · 주역 통합 인생 리포트',
  features: ['사주팔자 완전 분석', '대운 + 2026 월별 운세', '서양 점성술 통합', '주역 해석'],
  isLaunch: true,
};

const PLAN_ICONS: Record<string, string> = {
  basic: 'ri-sun-line', premium: 'ri-vip-crown-line', vip: 'ri-space-ship-line',
};
const PLAN_INCLUDES: Record<string, string[]> = {
  basic: ['일일 운세 리포트', '기본 성향 분석', '오행 요약'],
  premium: ['사주팔자 완전 분석', '대운 + 월별 운세', '점성술 + 주역 통합', '재물·연애·건강 분석'],
  vip: ['프리미엄 전체 포함', '사주+점성술+주역 3중 분석', 'AI 1:1 심층 채팅 무제한', '전문가 수준 커스텀 해석'],
};
const PLAN_ROUTES: Record<string, string> = {
  basic: '/basic-report', premium: '/premium-report', vip: '/vip-report',
};
const PAYMENT_METHODS = [
  { id: 'paypal' as const, name: 'PayPal', sub: '신용카드 · 체크카드 · 계좌 모두 지원', icon: 'ri-paypal-fill', gradient: 'linear-gradient(135deg, #003087, #009cde)', badge: '추천' },
  { id: 'stripe' as const, name: 'Stripe', sub: '해외 신용카드 · 체크카드', icon: 'ri-bank-card-fill', gradient: 'linear-gradient(135deg, #635bff, #7b72ff)', badge: null },
  { id: 'wise' as const, name: 'Wise', sub: '국제 계좌이체 (수수료 최소)', icon: 'ri-exchange-dollar-line', gradient: 'linear-gradient(135deg, #48bb78, #38a169)', badge: null },
] as const;

/* ─── Countdown Hook ─── */
function useCountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const key = 'destiny-launch-expiry';
    let expiry = parseInt(localStorage.getItem(key) ?? '0', 10);
    if (!expiry || expiry < Date.now()) {
      expiry = Date.now() + 23 * 60 * 60 * 1000 + 47 * 60 * 1000 + 33 * 1000;
      localStorage.setItem(key, String(expiry));
    }
    const tick = () => {
      const diff = Math.max(0, expiry - Date.now());
      setTimeLeft({ hours: Math.floor(diff / 3600000), minutes: Math.floor((diff % 3600000) / 60000), seconds: Math.floor((diff % 60000) / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

/* ─── PayPal global type ─── */
declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        style?: Record<string, unknown>;
        createOrder: (data: unknown, actions: { order: { create: (o: unknown) => Promise<string> } }) => Promise<string>;
        onApprove: (data: { orderID: string }, actions: { order: { capture: () => Promise<unknown> } }) => Promise<void>;
        onError?: (err: unknown) => void;
        onCancel?: () => void;
      }) => { render: (sel: string) => void };
    };
  }
}

export default function PaymentGatewayModal({ isOpen, onClose }: PaymentGatewayModalProps) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<PaymentMethodId | null>(null);
  const [plan, setPlan] = useState<PlanInfo>(DEFAULT_PLAN);
  const [phase, setPhase] = useState<ModalPhase>('select');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState('');
  const ppRendered = useRef(false);
  const { hours, minutes, seconds } = useCountdownTimer();

  /* reset on open */
  useEffect(() => {
    if (isOpen) {
      const raw = localStorage.getItem('destiny-selected-plan');
      if (raw) { try { setPlan(JSON.parse(raw) as PlanInfo); } catch { setPlan(DEFAULT_PLAN); } }
      setSelected(null); setPhase('select'); setEmail(''); setEmailError('');
      setSdkReady(false); setSdkError(''); ppRendered.current = false;
    }
  }, [isOpen]);

  /* load PayPal SDK when entering paypal-checkout phase */
  useEffect(() => {
    if (phase !== 'paypal-checkout') return;

    const existing = document.getElementById(PLAN_SDK_SCRIPT_ID);
    if (existing) { setSdkReady(true); return; }

    // Remove conflicting PayPal scripts to avoid multi-instance error
    document.querySelectorAll('script[src*="paypal.com/sdk/js"]').forEach((s) => {
      if (s.id !== PLAN_SDK_SCRIPT_ID) s.remove();
    });
    if ((window as { paypal?: unknown }).paypal) delete (window as { paypal?: unknown }).paypal;

    const script = document.createElement('script');
    script.id = PLAN_SDK_SCRIPT_ID;
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture`;
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => setSdkError('PayPal을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
    document.body.appendChild(script);
  }, [phase]);

  /* render PayPal buttons */
  const renderPayPal = useCallback(() => {
    if (!window.paypal || ppRendered.current) return;
    ppRendered.current = true;
    window.paypal.Buttons({
      style: { shape: 'pill', color: 'gold', layout: 'vertical', label: 'pay' },
      createOrder: (_data, actions) =>
        actions.order.create({
          purchase_units: [{
            amount: { value: plan.price.toFixed(2), currency_code: 'USD' },
            description: `${plan.name} Plan — 운명 분석 리포트`,
          }],
        }),
      onApprove: async (_data, actions) => {
        await actions.order.capture();
        localStorage.setItem('destiny-report-email', email);
        setPhase('processing');
        setTimeout(() => {
          setPhase('success');
          setTimeout(() => { onClose(); navigate(PLAN_ROUTES[plan.id] ?? '/premium-report'); }, 2200);
        }, 1800);
      },
      onError: () => setSdkError('결제 중 오류가 발생했습니다. 다시 시도해주세요.'),
      onCancel: () => { ppRendered.current = false; },
    }).render('#paypal-gateway-btn');
  }, [plan, email, navigate, onClose]);

  useEffect(() => {
    if (sdkReady && phase === 'paypal-checkout') {
      setTimeout(renderPayPal, 100);
    }
  }, [sdkReady, phase, renderPayPal]);

  if (!isOpen) return null;

  const isVip = plan.id === 'vip';
  const isPremium = plan.id === 'premium';
  const discount = plan.originalPrice ? Math.round((1 - plan.price / plan.originalPrice) * 100) : 0;
  const accentColor = isVip ? 'rgba(139,92,246,1)' : isPremium ? 'rgba(251,191,36,1)' : 'rgba(255,255,255,0.8)';
  const buttonClass = isVip ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white' : 'bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black';
  const buttonGlow = isVip ? '0 0 30px rgba(139,92,246,0.4)' : '0 0 30px rgba(251,191,36,0.35)';
  const featureList = PLAN_INCLUDES[plan.id] || plan.features;

  const validateEmail = (val: string) => {
    if (!val) { setEmailError('이메일을 입력해주세요'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { setEmailError('올바른 이메일 형식을 입력해주세요'); return false; }
    setEmailError(''); return true;
  };

  const handlePayClick = () => { if (selected) setPhase('email'); };
  const handleEmailSubmit = () => {
    if (!validateEmail(email)) return;
    if (selected === 'paypal') { setPhase('paypal-checkout'); return; }
    // Stripe / Wise — simulate for now
    setPhase('processing');
    setTimeout(() => {
      setPhase('success');
      setTimeout(() => { onClose(); navigate(PLAN_ROUTES[plan.id] ?? '/premium-report'); }, 2200);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={phase === 'select' ? onClose : undefined} />

      <div className="relative w-full max-w-md rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{
          background: 'linear-gradient(135deg, #0d0820 0%, #0a0618 50%, #0d0820 100%)',
          border: `1px solid ${isVip ? 'rgba(139,92,246,0.45)' : 'rgba(251,191,36,0.35)'}`,
          boxShadow: `0 0 100px ${isVip ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.2)'}, 0 0 0 1px rgba(255,255,255,0.05) inset`,
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />

        {phase !== 'processing' && phase !== 'success' && (
          <button onClick={onClose}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white/60 hover:text-white transition-all cursor-pointer"
          ><i className="ri-close-line text-lg"></i></button>
        )}

        {/* ── Processing ── */}
        {phase === 'processing' && (
          <div className="p-10 flex flex-col items-center justify-center min-h-[300px]">
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full border-2 border-purple-400/30 animate-spin" style={{ animationDuration: '1.5s' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <i className={`${PLAN_ICONS[plan.id] || 'ri-vip-crown-line'} text-2xl ${isVip ? 'text-purple-400' : 'text-amber-400'}`}></i>
              </div>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">결제 처리 중...</h3>
            <p className="text-sm text-purple-300/70 text-center">리포트를 생성하고 이메일로 전송하고 있습니다</p>
            <p className="text-xs text-amber-400/60 mt-2">{email}</p>
          </div>
        )}

        {/* ── Success ── */}
        {phase === 'success' && (
          <div className="p-10 flex flex-col items-center justify-center min-h-[300px]">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
              style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.2), rgba(16,185,129,0.15))', border: '2px solid rgba(52,211,153,0.5)' }}
            >
              <i className="ri-check-line text-2xl text-emerald-400"></i>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">결제 완료!</h3>
            <p className="text-sm text-purple-300/70 text-center mb-3">{plan.nameKr} 리포트가 생성되었습니다</p>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
              style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)' }}
            >
              <i className="ri-mail-check-line text-emerald-400 text-sm"></i>
              <span className="text-xs text-emerald-300 font-semibold">PDF가 이메일로 전송되었습니다</span>
            </div>
            <p className="text-xs text-purple-400/50 mt-2">{email}</p>
            <p className="text-xs text-purple-400/40 mt-3">잠시 후 리포트 페이지로 이동합니다...</p>
          </div>
        )}

        {/* ── Select ── */}
        {phase === 'select' && (
          <div className="p-7">
            <div className="text-center mb-5">
              <div className="relative w-14 h-14 mx-auto mb-3">
                <div className="absolute inset-0 rounded-full blur-xl animate-pulse"
                  style={{ background: isVip ? 'rgba(139,92,246,0.3)' : 'rgba(251,191,36,0.25)' }} />
                <div className="relative w-full h-full rounded-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${isVip ? '#7c3aed, #a855f7' : '#fbbf24, #f59e0b'})` }}
                >
                  <i className={`${PLAN_ICONS[plan.id] || 'ri-vip-crown-line'} text-xl ${isVip ? 'text-white' : 'text-black'}`}></i>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-0.5">결제 수단 선택</h3>
              <p className="text-xs text-purple-300/70">{plan.nameKr} 플랜 결제 진행</p>
            </div>

            {plan.originalPrice && (
              <div className="flex items-center justify-between rounded-2xl px-4 py-3 mb-4"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
              >
                <div className="flex items-center gap-1.5">
                  <i className="ri-timer-flash-line text-rose-400 text-sm animate-pulse"></i>
                  <span className="text-xs font-bold text-rose-400">런칭가 마감</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {[{ val: hours, label: '시' }, { val: minutes, label: '분' }, { val: seconds, label: '초' }].map((item, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <div className="text-center">
                        <div className="text-base font-black text-rose-300 tabular-nums">{String(item.val).padStart(2, '0')}</div>
                        <div className="text-[8px] text-rose-400/50">{item.label}</div>
                      </div>
                      {i < 2 && <span className="text-rose-400/60 text-sm font-black mb-2">:</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl p-4 mb-5"
              style={{ background: isVip ? 'rgba(139,92,246,0.08)' : 'rgba(251,191,36,0.07)', border: `1px solid ${isVip ? 'rgba(139,92,246,0.3)' : 'rgba(251,191,36,0.2)'}` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-black ${isVip ? 'text-purple-300' : 'text-primary-gold'}`}>{plan.name} Plan</span>
                    {plan.isLaunch && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-black bg-rose-400">런칭특가</span>}
                  </div>
                  <p className="text-xs text-white/60 mb-2">{plan.desc}</p>
                  <div className="space-y-1">
                    {featureList.slice(0, 3).map((f) => (
                      <div key={f} className="flex items-center gap-1.5 text-[11px] text-white/70">
                        <i className={`ri-check-line text-xs ${isVip ? 'text-purple-400' : 'text-emerald-400'}`}></i>{f}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {plan.originalPrice && (
                    <div className="flex items-center justify-end gap-1.5 mb-0.5">
                      <span className="text-xs line-through text-white/30">${plan.originalPrice}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white bg-rose-500/80">-{discount}%</span>
                    </div>
                  )}
                  <p className={`text-3xl font-black leading-none ${isVip ? 'text-purple-300' : 'text-primary-gold'}`}
                    style={{ textShadow: isVip ? '0 0 15px rgba(139,92,246,0.5)' : '0 0 15px rgba(251,191,36,0.5)' }}
                  >${plan.price}</p>
                  <p className="text-[9px] text-white/30 mt-0.5">USD · 1회</p>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-[10px] text-purple-400/60 font-semibold uppercase tracking-widest mb-2">결제 수단 선택</p>
              {PAYMENT_METHODS.map((method) => (
                <button key={method.id} onClick={() => setSelected(method.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${selected === method.id ? 'border-primary-gold/60 bg-primary-gold/8' : 'border-white/8 bg-white/3 hover:border-white/18 hover:bg-white/5'}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: method.gradient }}>
                    <i className={`${method.icon} text-xl text-white`}></i>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{method.name}</span>
                      {method.badge && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-bold">{method.badge}</span>
                      )}
                    </div>
                    <p className="text-xs text-purple-400/60">{method.sub}</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${selected === method.id ? 'border-primary-gold bg-primary-gold' : 'border-white/20'}`}>
                    {selected === method.id && <i className="ri-check-line text-[8px] text-black"></i>}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4 mb-4">
              {[
                { icon: 'ri-shield-check-fill', color: 'text-emerald-400', label: 'SSL 보안' },
                { icon: 'ri-lock-fill', color: 'text-primary-gold/60', label: '256-bit 암호화' },
                { icon: 'ri-refund-2-line', color: 'text-purple-400/60', label: '즉시 발급' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-1">
                  <i className={`${b.icon} ${b.color} text-sm`}></i>
                  <span className="text-[10px] text-purple-400/50">{b.label}</span>
                </div>
              ))}
            </div>

            <button onClick={handlePayClick} disabled={!selected}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap ${selected ? `${buttonClass} hover:scale-[1.02]` : 'bg-white/8 text-white/25 cursor-not-allowed'}`}
              style={selected ? { boxShadow: buttonGlow } : {}}
            >
              <i className="ri-secure-payment-fill"></i>
              {selected ? `다음 단계 — $${plan.price}` : '결제 수단을 선택하세요'}
              {selected && <i className="ri-arrow-right-line"></i>}
            </button>
          </div>
        )}

        {/* ── Email ── */}
        {phase === 'email' && (
          <div className="p-7">
            <button onClick={() => setPhase('select')}
              className="flex items-center gap-1.5 text-xs text-purple-400/60 hover:text-purple-300 transition-colors cursor-pointer mb-5"
            ><i className="ri-arrow-left-line"></i>결제 수단 변경</button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
                style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}
              >
                <i className="ri-mail-send-fill text-emerald-400 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">이메일 입력</h3>
              <p className="text-xs text-purple-300/70">결제 완료 후 PDF 리포트를 이메일로 발송해드립니다</p>
            </div>

            <div className="flex items-center justify-between rounded-2xl px-4 py-3 mb-5"
              style={{ background: isVip ? 'rgba(139,92,246,0.08)' : 'rgba(251,191,36,0.07)', border: `1px solid ${isVip ? 'rgba(139,92,246,0.3)' : 'rgba(251,191,36,0.2)'}` }}
            >
              <div className="flex items-center gap-2">
                <i className={`${PLAN_ICONS[plan.id]} text-sm ${isVip ? 'text-purple-400' : 'text-amber-400'}`}></i>
                <span className={`text-sm font-bold ${isVip ? 'text-purple-300' : 'text-amber-400'}`}>{plan.name} Plan</span>
              </div>
              <div className="text-right">
                {plan.originalPrice && <span className="text-xs line-through text-white/30 mr-1.5">${plan.originalPrice}</span>}
                <span className={`text-lg font-black ${isVip ? 'text-purple-300' : 'text-amber-400'}`}>${plan.price}</span>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-purple-300/80 mb-2">
                <i className="ri-mail-line mr-1"></i>이메일 주소
              </label>
              <input type="email" name="email" value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                placeholder="example@email.com"
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-purple-400/40 focus:outline-none transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: emailError ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(139,92,246,0.3)' }}
              />
              {emailError && (
                <p className="text-xs text-rose-400 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{emailError}
                </p>
              )}
              <p className="text-[10px] text-purple-400/40 mt-1.5">
                <i className="ri-shield-check-line mr-1"></i>이메일은 리포트 전송에만 사용됩니다
              </p>
            </div>

            <button onClick={handleEmailSubmit}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap hover:scale-[1.02] ${buttonClass}`}
              style={{ boxShadow: buttonGlow }}
            >
              <i className="ri-secure-payment-fill"></i>
              {selected === 'paypal' ? 'PayPal로 결제하기' : `$${plan.price} 결제 완료하기`}
              <i className="ri-arrow-right-line"></i>
            </button>
            <p className="text-[10px] text-center text-purple-400/40 mt-3">결제 후 즉시 리포트 페이지로 이동합니다</p>
          </div>
        )}

        {/* ── PayPal Checkout ── */}
        {phase === 'paypal-checkout' && (
          <div className="p-7">
            <button onClick={() => { setPhase('email'); ppRendered.current = false; }}
              className="flex items-center gap-1.5 text-xs text-purple-400/60 hover:text-purple-300 transition-colors cursor-pointer mb-5"
            ><i className="ri-arrow-left-line"></i>이메일 변경</button>

            <div className="text-center mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3"
                style={{ background: 'rgba(0,48,135,0.3)', border: '1px solid rgba(0,156,222,0.4)' }}
              >
                <i className="ri-paypal-fill text-2xl text-cyan-300"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">PayPal 결제</h3>
              <p className="text-xs text-purple-300/70">아래 버튼을 클릭해 PayPal 결제를 진행하세요</p>
            </div>

            {/* Plan + email summary */}
            <div className="rounded-xl px-4 py-3 mb-5 flex items-center justify-between"
              style={{ background: isVip ? 'rgba(139,92,246,0.08)' : 'rgba(251,191,36,0.07)', border: `1px solid ${isVip ? 'rgba(139,92,246,0.25)' : 'rgba(251,191,36,0.2)'}` }}
            >
              <div>
                <p className={`text-sm font-bold ${isVip ? 'text-purple-300' : 'text-amber-400'}`}>{plan.name} Plan</p>
                <p className="text-xs text-white/40 mt-0.5">{email}</p>
              </div>
              <p className={`text-2xl font-black ${isVip ? 'text-purple-300' : 'text-primary-gold'}`}>${plan.price}</p>
            </div>

            {/* PayPal button container */}
            {sdkError ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm text-center mb-4">
                <i className="ri-error-warning-fill mr-2"></i>{sdkError}
                <button onClick={() => { setSdkError(''); ppRendered.current = false; setSdkReady(false); setPhase('email'); setPhase('paypal-checkout'); }}
                  className="block w-full mt-2 text-xs text-red-300 underline cursor-pointer"
                >다시 시도</button>
              </div>
            ) : !sdkReady ? (
              <div className="flex items-center justify-center gap-3 py-8 text-gray-400">
                <div className="w-5 h-5 border-2 border-cyan-400/40 border-t-cyan-400 rounded-full animate-spin"></div>
                <span className="text-sm">PayPal 로딩 중...</span>
              </div>
            ) : null}

            <div id="paypal-gateway-btn" className="min-h-[50px]"></div>

            <div className="flex items-center justify-center gap-2 mt-4 text-gray-500 text-xs">
              <i className="ri-shield-check-fill text-emerald-400"></i>
              <span>PayPal 구매자 보호 · SSL 암호화 결제</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
